import { Component, effect, input, output, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTreeFlatDataSource, MatTreeFlattener, MatTreeModule } from '@angular/material/tree';
import { FlatNode, SHOW_MORE_NODE, TreeNode } from './tree.model';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
  selector: 'cbre-tree',
  imports: [CommonModule, MatTreeModule, MatIconModule, MatProgressSpinnerModule],
  templateUrl: './tree.component.html',
  styleUrl: './tree.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TreeComponent {
  /**
   * The tree control for the tree
   */
  treeControl: FlatTreeControl<FlatNode>;

  /**
   * The tree flattener for the tree
   */
  treeFlattener: MatTreeFlattener<TreeNode, FlatNode>;

  /**
   * The data source for the tree
   */
  dataSource: MatTreeFlatDataSource<TreeNode, FlatNode>;
  public clickedShowMoreNode: FlatNode | null = null;
  public loadingNodes: Map<string, boolean> = new Map();
  public isLoading = false;
  public paddingMap = { 1: 20, 2: 40 };

  private readonly expandedNodes = new Set<string>();

  readonly treeData = input<TreeNode[]>([]);
  readonly currentRecordId = input<number>();
  readonly emitLoadMore = output<FlatNode>();

  constructor() {
    this.treeFlattener = new MatTreeFlattener(this.transformer, this.getLevel, this.isExpandable, this.getChildren);
    this.treeControl = new FlatTreeControl<FlatNode>(this.getLevel, this.isExpandable);
    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

    effect(() => {
      const data = this.treeData();
      if (data) {
        this.dataSource.data = data;
      }
    });

    effect(() => {
      const id = this.currentRecordId();
      if (id != null && this.treeControl.dataNodes?.length) {
        const nodeToExpand = this.findNodeToExpand(this.treeControl.dataNodes, id);
        if (nodeToExpand) {
          this.expandNodeAndParents(this.treeControl.dataNodes, nodeToExpand);
        }
      }
    });
  }

  getLevel = (node: FlatNode) => node.level;

  isExpandable = (node: FlatNode) => node.expandable;

  getChildren = (node: TreeNode): TreeNode[] => node.direct_children ?? [];

  hasChild = (_: number, node: FlatNode) => node.expandable;

  transformer = (node: TreeNode, level: number) => {
    return {
      ...node,
      expandable: !!node.direct_children && node.direct_children.length > 0,
      level: level,
    };
  };

  /**
   * Find a node by its ID
   */
  public findNodeToExpand(nodes: FlatNode[], id: number): FlatNode | undefined {
    const currentRecordNode = nodes?.find(node => node.id === id);
    return nodes?.find(node => node.level === currentRecordNode?.level - 1);
  }

  /**
   * Find the parent node of a given node
   */
  public findParentNode(nodes: FlatNode[], childNode: FlatNode): FlatNode | undefined {
    if (!nodes || !childNode) return undefined;

    const childIndex = nodes.indexOf(childNode);
    if (childIndex === -1) return undefined;

    return nodes
      .slice(0, childIndex)
      .reverse()
      .find(node => node.level < childNode.level);
  }

  /**
   * Recursively expand a node and its parent nodes
   */
  public expandNodeAndParents(nodes: FlatNode[], node: FlatNode | undefined): void {
    if (!node) return;

    this.treeControl.expand(node);
    const parentNode = this.findParentNode(nodes, node);
    this.expandNodeAndParents(nodes, parentNode);
  }

  isNodeLoading(node: FlatNode): boolean {
    if (node.name === SHOW_MORE_NODE.name) {
      return this.clickedShowMoreNode === node;
    }
    return false;
  }

  resetLoadingState(): void {
    this.clickedShowMoreNode = null;
  }

  onNodeToggle(node: FlatNode, event: Event): void {
    event.stopPropagation();

    if (this.treeControl.isExpanded(node)) {
      this.treeControl.collapse(node);
      if (!this.expandedNodes.has(String(node.id))) {
        this.expandedNodes.add(String(node.id));
      }
      return;
    }

    // Only call API on first expansion
    if (!this.expandedNodes.has(String(node.id))) {
      this.loadingNodes[node.id] = true;
      this.emitLoadMore.emit(node); // Parent will handle API and call expandNode when done
    } else {
      this.treeControl.expand(node);
    }
  }

  // Called by parent after data is loaded
  public expandNode(node: FlatNode) {
    this.expandedNodes.add(String(node.id));
    this.loadingNodes[node.id] = false;
    // Find the current node instance in dataNodes by id
    const currentNode = this.treeControl.dataNodes.find(n => n.id === node.id && n.level === node.level);
    if (currentNode) {
      this.treeControl.expand(currentNode);
    }
  }

  onShowMoreClick(node: FlatNode, event: Event): void {
    event.stopPropagation();
    this.clickedShowMoreNode = node;
    this.emitLoadMore.emit(node);
  }
}
